import { useEffect, useRef } from "react";
import { useSession_ } from "@/hooks/use-session";
import { useWebchatSocket } from "@/hooks/use-socket";
import { HostBridge } from "@/lib/host-bridge";
import { applyWidgetTheme, useSession } from "@/store/session-store";
import { Launcher } from "@/components/launcher";
import { ChatWindow } from "@/components/chat-window";

/**
 * Root component of the widget. Mounts at #root inside the iframe and is
 * the full React tree. Two hooks bootstrap in order:
 *   1. useSession_  — POST /api/webchat/session, hydrates Zustand store
 *   2. useWebchatSocket — connects to /webchat namespace with session JWT
 *
 * The HostBridge syncs open/close state with the surrounding iframe so the
 * loader can resize/minimize it appropriately.
 */
export function App() {
  const { api } = useSession_();
  useWebchatSocket();

  const open = useSession((s) => s.open);
  const setOpen = useSession((s) => s.setOpen);
  const setHostTheme = useSession((s) => s.setHostTheme);
  const setLocale = useSession((s) => s.setLocale);
  const position = useSession((s) => s.branding?.position);
  const status = useSession((s) => s.status);
  const bridgeRef = useRef<HostBridge | null>(null);

  // Host ↔ iframe message bridge. Receives host-commands (open/close) and
  // late identity/theme/locale updates. Posts resize/position notifications
  // outward.
  useEffect(() => {
    bridgeRef.current = new HostBridge({
      onSettings: (settings) => {
        // Late identity push is swallowed because the session has already
        // started; re-keying mid-session would confuse routing + read
        // receipts. But theme and locale changes are safe to apply live —
        // the host may change its color scheme or language while the
        // widget is open.
        if (settings.hostTheme) {
          setHostTheme(settings.hostTheme);
          const branding = useSession.getState().branding;
          if (branding) applyWidgetTheme(branding, settings.hostTheme);
        }
        if (settings.locale) {
          setLocale(settings.locale);
        }
      },
      onHostCommand: (command) => {
        if (command === "open") setOpen(true);
        else if (command === "close") setOpen(false);
      },
    });
    return () => {
      bridgeRef.current?.destroy();
      bridgeRef.current = null;
    };
  }, [setOpen, setHostTheme, setLocale]);

  useEffect(() => {
    bridgeRef.current?.notifyOpen(open);
  }, [open]);

  useEffect(() => {
    if (position) bridgeRef.current?.notifyPosition(position);
  }, [position]);

  // Don't render any UI until the bootstrap call completes — avoids a flash
  // of unbranded launcher before the channel's primary color, avatar, etc.
  // are loaded. On error we also stay hidden so a broken install doesn't
  // expose a non-functional UI to visitors.
  if (status !== "ready") return null;

  return (
    <>
      <Launcher />
      <ChatWindow api={api} />
    </>
  );
}
